import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  useColorModeValue,
  IconButton,
  Tooltip,
  Alert,
  AlertIcon,
} from '@chakra-ui/react';
import { FaUndo, FaTrash } from 'react-icons/fa';
import SignatureCanvas from 'react-signature-canvas';

/**
 * Componente de lienzo para dibujar firmas digitales
 * Permite al usuario dibujar su firma con el mouse o touchscreen
 * y convertirla a formato PNG
 */
const SignatureCanvasComponent = ({ onSignatureSave, onCancel }) => {
  const sigCanvas = useRef(null);
  const [isEmpty, setIsEmpty] = useState(true);
  const [canvasSize, setCanvasSize] = useState({ width: 500, height: 200 });

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.300', 'gray.600');
  const canvasBg = useColorModeValue('#ffffff', '#2D3748');

  // Ajustar tamaño del canvas según el ancho de la ventana
  useEffect(() => {
    const updateCanvasSize = () => {
      const containerWidth = Math.min(window.innerWidth - 100, 600);
      setCanvasSize({
        width: containerWidth,
        height: Math.min(containerWidth * 0.4, 200),
      });
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  // Verificar si el canvas está vacío después de cada trazo
  const handleEnd = () => {
    if (sigCanvas.current) {
      setIsEmpty(sigCanvas.current.isEmpty());
    }
  };

  // Limpiar el canvas completamente
  const handleClear = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setIsEmpty(true);
    }
  };

  // Deshacer el último trazo
  const handleUndo = () => {
    if (sigCanvas.current) {
      const data = sigCanvas.current.toData();
      if (data.length > 0) {
        data.pop(); // Eliminar el último trazo
        sigCanvas.current.fromData(data);
        setIsEmpty(data.length === 0);
      }
    }
  };

  // Guardar la firma como PNG
  const handleSave = () => {
    if (sigCanvas.current && !isEmpty) {
      // Convertir el canvas a blob PNG
      sigCanvas.current.getCanvas().toBlob((blob) => {
        if (blob) {
          // Crear un archivo File desde el blob
          const file = new File([blob], 'signature.png', {
            type: 'image/png',
            lastModified: Date.now(),
          });
          onSignatureSave(file);
        }
      }, 'image/png');
    }
  };

  return (
    <VStack spacing={4} align="stretch" w="100%">
      {/* Instrucciones */}
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Text fontSize="sm">
          Dibuja tu firma usando el mouse o pantalla táctil. Asegúrate de que sea clara y legible.
        </Text>
      </Alert>

      {/* Canvas de firma */}
      <Box
        borderWidth="2px"
        borderColor={borderColor}
        borderRadius="lg"
        p={2}
        bg={bgColor}
        boxShadow="sm"
      >
        <Box
          position="relative"
          bg={canvasBg}
          borderRadius="md"
          overflow="hidden"
          border="1px dashed"
          borderColor={borderColor}
        >
          <SignatureCanvas
            ref={sigCanvas}
            canvasProps={{
              width: canvasSize.width,
              height: canvasSize.height,
              className: 'signature-canvas',
              style: { width: '100%', height: '100%' },
            }}
            backgroundColor={canvasBg}
            penColor="#000000"
            minWidth={1.5}
            maxWidth={3}
            onEnd={handleEnd}
          />
        </Box>

        {/* Controles del canvas */}
        <HStack spacing={2} mt={3} justify="space-between">
          <HStack spacing={2}>
            <Tooltip label="Deshacer último trazo" placement="top">
              <IconButton
                icon={<FaUndo />}
                size="sm"
                colorScheme="blue"
                variant="outline"
                onClick={handleUndo}
                isDisabled={isEmpty}
                aria-label="Deshacer"
              />
            </Tooltip>

            <Tooltip label="Limpiar todo" placement="top">
              <IconButton
                icon={<FaTrash />}
                size="sm"
                colorScheme="red"
                variant="outline"
                onClick={handleClear}
                isDisabled={isEmpty}
                aria-label="Limpiar"
              />
            </Tooltip>
          </HStack>

          <Text fontSize="xs" color="gray.500">
            {isEmpty ? 'Canvas vacío' : 'Firma dibujada'}
          </Text>
        </HStack>
      </Box>

      {/* Botones de acción */}
      <HStack spacing={3} justify="flex-end">
        <Button
          variant="ghost"
          onClick={onCancel}
        >
          Cancelar
        </Button>
        <Button
          colorScheme="blue"
          onClick={handleSave}
          isDisabled={isEmpty}
        >
          Guardar Firma
        </Button>
      </HStack>

      {/* Advertencia sobre firma vacía */}
      {isEmpty && (
        <Alert status="warning" borderRadius="md" fontSize="sm">
          <AlertIcon />
          Debes dibujar tu firma antes de guardar
        </Alert>
      )}
    </VStack>
  );
};

export default SignatureCanvasComponent;
